import { forwardRef, useEffect, useRef, type InputHTMLAttributes } from "react";
import styled, { css } from "styled-components";

import { CheckIcon as CheckGlyph, MinusIcon as MinusGlyph } from "../../icons/glyphs";
import { NESTED_INTERACTIVE_ATTR } from "../../utils/nestedInteractive";
import { useFieldControl } from "../Field/FieldContext";
import { controlFocusRing, controlRadius, controlTransition } from "../foundations/controlStyles";

const HiddenInput = styled.input`
  position: absolute;
  opacity: 0;
  width: 0;
  height: 0;
  pointer-events: none;
`;

const Box = styled.span<{
  $checked: boolean;
  $indeterminate: boolean;
  $disabled: boolean;
  $size: string;
}>`
  display: inline-flex;
  /* 글리프를 out-of-flow 로 얹는 기준면. 중앙 정렬은 글리프가 직접 한다(glyphLayout 주석 참조). */
  position: relative;
  width: ${({ $size }) => $size};
  height: ${({ $size }) => $size};
  ${controlRadius}
  flex-shrink: 0;
  cursor: ${({ $disabled }) => ($disabled ? "not-allowed" : "pointer")};
  ${controlTransition("background-color", "border-color")}
  background-color: ${({ $checked, $indeterminate, $disabled, theme }) => {
    if ($disabled) return theme.color.bg.control.disabled;
    if ($checked || $indeterminate) return theme.color.bg.control.primary;
    return theme.color.bg.surfaceRaised;
  }};
  border: 1.5px solid
    ${({ $checked, $indeterminate, $disabled, theme }) => {
      if ($disabled) return theme.color.border.disabled;
      if ($checked || $indeterminate) return theme.color.border.selected;
      // unchecked는 외곽선이 유일한 control 경계다. elevation 면 토큰(surfaceOverlay)을
      // border로 빌리면 disabled 외곽과 거의 같은 명도로 수렴하므로, 모든 surface에서
      // 3:1을 지키는 중립 interaction 경계로 canonical rest를 고정한다.
      return theme.color.border.strong;
    }};

  &:hover {
    ${({ $disabled, $checked, $indeterminate, theme }) => {
      if ($disabled) return "";
      if ($checked || $indeterminate) {
        return `
          background-color: ${theme.color.bg.control.primaryHover};
          border-color: ${theme.color.border.selectedHover};
        `;
      }
      return `border-color: ${theme.color.border.selectedHover};`;
    }}
  }

  &:active,
  ${HiddenInput}:active + & {
    ${({ $disabled, $checked, $indeterminate, theme }) => {
      if ($disabled) return "";
      if ($checked || $indeterminate) {
        return `
          background-color: ${theme.color.bg.control.primaryActive};
          border-color: ${theme.color.border.selectedActive};
        `;
      }
      return `border-color: ${theme.color.border.selectedActive};`;
    }}
  }
`;

// 공용 글리프에 disabled 색만 입혀 쓴다. 크기(70%/60%)는 호출부에서 width/height로 전달.
const iconColor = css<{ $disabled: boolean }>`
  color: ${({ $disabled, theme }) =>
    $disabled ? theme.color.text.muted : theme.color.text.onControl};
`;

/* 글리프는 in-flow 자식으로 두지 않는다. flex 컨테이너의 baseline 은 첫 in-flow 자식에서
   합성되므로, 체크 상태에 따라 글리프가 생겼다 없어지면 Box 의 baseline 이 바뀌고 Box 를 담은
   line box 높이까지 흔들린다(전체선택 토글에 목록이 4px 튀던 원인 — PIT-state-dependent-inline-baseline).
   absolute + inset 0 + margin auto 는 flex 중앙 정렬과 같은 위치·크기를 주면서, baseline 을
   세 상태 모두 border-box 하단으로 고정한다. */
const glyphLayout = css`
  position: absolute;
  inset: 0;
  margin: auto;
`;

const CheckIcon = styled(CheckGlyph)<{ $disabled: boolean }>`
  ${iconColor}
  ${glyphLayout}
`;

const IndeterminateIcon = styled(MinusGlyph)<{ $disabled: boolean }>`
  ${iconColor}
  ${glyphLayout}
`;

const Wrapper = styled.div<{ $disabled: boolean }>`
  display: inline-flex;
  align-items: center;
  cursor: ${({ $disabled }) => ($disabled ? "not-allowed" : "pointer")};
  /* Box는 span 오버레이라 드래그 시 선택이 개시돼 주변 컨텐츠까지 선택된다. 상속으로 Box·글리프까지 차단. */
  user-select: none;

  ${HiddenInput}:focus-visible + ${Box} {
    ${controlFocusRing()}
  }
`;

export interface CheckboxProps extends Omit<
  InputHTMLAttributes<HTMLInputElement>,
  "type" | "size" | "defaultChecked"
> {
  checked: boolean;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  /** Partial selection state. */
  indeterminate?: boolean;
  /** Box size. 기본 `1.7em` — 주변 폰트에 비례해 스케일(Switch/아이콘 정합, 16px 기준 기존 `1.7rem`과 동일 크기). 고정 크기는 `"1.7rem"`처럼 길이값으로 override. */
  size?: string;
}

/**
 * Controlled checkbox with native input semantics.
 * 라벨이 필요하면 `<Field>`로 감싸세요 — 자체 `label` prop은 없습니다.
 */
export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(
  (
    {
      checked,
      onChange,
      indeterminate = false,
      disabled = false,
      size = "1.7em",
      className,
      style,
      id,
      ...rest
    },
    forwardedRef,
  ) => {
    const localRef = useRef<HTMLInputElement | null>(null);
    const { controlId, disabled: isDisabled } = useFieldControl({
      componentName: "Checkbox",
      id,
      disabled,
    });

    const setRef = (node: HTMLInputElement | null) => {
      localRef.current = node;
      if (typeof forwardedRef === "function") {
        forwardedRef(node);
      } else if (forwardedRef) {
        forwardedRef.current = node;
      }
    };

    // Browsers clear the native indeterminate flag after user interaction.
    useEffect(() => {
      if (localRef.current) {
        localRef.current.indeterminate = indeterminate;
      }
    }, [indeterminate, checked]);

    const handleBoxClick = () => {
      localRef.current?.focus();
      localRef.current?.click();
    };

    return (
      <Wrapper $disabled={isDisabled} className={className} style={style}>
        <HiddenInput
          ref={setRef}
          type="checkbox"
          id={controlId}
          checked={checked}
          disabled={isDisabled}
          onChange={onChange}
          {...rest}
        />
        <Box
          // 실제 input은 시각적으로 숨겨져 있고 이 span이 보이는 클릭 대상이라, 중첩
          // 인터랙티브 판정(clickable 행/카드)이 태그·role만으로는 잡지 못한다.
          {...{ [NESTED_INTERACTIVE_ATTR]: "" }}
          onClick={handleBoxClick}
          $checked={checked}
          $indeterminate={indeterminate}
          $disabled={isDisabled}
          $size={size}
        >
          {indeterminate ? (
            <IndeterminateIcon $disabled={isDisabled} width="60%" height="60%" />
          ) : checked ? (
            <CheckIcon $disabled={isDisabled} width="70%" height="70%" />
          ) : null}
        </Box>
      </Wrapper>
    );
  },
);

Checkbox.displayName = "Checkbox";
